import React, { useEffect } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Alert,
    Divider,
} from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useCustomPageController } from "./context/custom-page-controller";
import { useCustomPageState } from "./context/custom-page-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

export function CustomPageComponent() {
    const { loadArticle } = useCustomPageController();
    const { article, loading, error } = useCustomPageState();
    const { idArticle } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            if (idArticle) {
                await loadArticle(Number(idArticle));
            }
        };
        fetchData();
    }, [idArticle]);

    if (loading) {
        return (
            <Box>
                <PageHeader
                    title="Carregando..."
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Carregando...' }
                    ]}
                />
                <Box>
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="30%" height={40} sx={{ mb: 3 }} />
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <PageHeader
                    title="Erro"
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Erro' }
                    ]}
                />
                <Alert severity="error" sx={{ mt: 3 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!article) {
        return (
            <Box>
                <PageHeader
                    title="Artigo não encontrado"
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Não encontrado' }
                    ]}
                />
                <Alert severity="warning" sx={{ mt: 3 }}>
                    O artigo solicitado não foi encontrado.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={article.title || ''}
                subtitle=""
                breadcrumbs={[
                    { label: article.title || '' }
                ]}
            />

            <Paper sx={{ p: 4 }} elevation={2}>
                {/* Cabeçalho com metadados */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Criado em: {article.createdDate
                                    ? dayjs(article.createdDate).format('DD/MM/YYYY')
                                    : 'N/A'}
                            </Typography>
                        </Box>

                        {article.modifiedDate && (
                            <>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2" color="text.secondary">
                                    Atualizado em: {dayjs(article.modifiedDate).format('DD/MM/YYYY')}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Conteúdo do artigo */}
                <Box
                    sx={{
                        // Normalização global - remove estilos inline
                        '& *': {
                            fontFamily: 'inherit !important',
                            margin: '0 !important',
                            padding: '0 !important',
                        },
                        '& p, & div, & span': {
                            fontSize: '1rem !important',
                            lineHeight: '1.8 !important',
                            marginBottom: '1rem !important',
                            marginTop: '0 !important',
                            paddingLeft: '0 !important',
                            paddingRight: '0 !important',
                        },
                        '& p:last-child, & div:last-child': {
                            marginBottom: '0 !important',
                        },
                        '& strong, & b': {
                            fontWeight: '600 !important',
                        },
                        '& em, & i': {
                            fontStyle: 'italic !important',
                        },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1,
                            marginTop: '1.5rem !important',
                            marginBottom: '1.5rem !important',
                            marginLeft: '0 !important',
                            marginRight: '0 !important',
                        },
                        '& h1': {
                            fontSize: '2rem !important',
                            fontWeight: '700 !important',
                            marginTop: '2rem !important',
                            marginBottom: '1rem !important',
                            lineHeight: '1.3 !important',
                        },
                        '& h2': {
                            fontSize: '1.75rem !important',
                            fontWeight: '700 !important',
                            marginTop: '2rem !important',
                            marginBottom: '1rem !important',
                            lineHeight: '1.3 !important',
                        },
                        '& h3': {
                            fontSize: '1.5rem !important',
                            fontWeight: '600 !important',
                            marginTop: '1.75rem !important',
                            marginBottom: '0.875rem !important',
                            lineHeight: '1.4 !important',
                        },
                        '& h4': {
                            fontSize: '1.25rem !important',
                            fontWeight: '600 !important',
                            marginTop: '1.5rem !important',
                            marginBottom: '0.75rem !important',
                            lineHeight: '1.4 !important',
                        },
                        '& h5': {
                            fontSize: '1.125rem !important',
                            fontWeight: '600 !important',
                            marginTop: '1.5rem !important',
                            marginBottom: '0.75rem !important',
                            lineHeight: '1.4 !important',
                        },
                        '& h6': {
                            fontSize: '1rem !important',
                            fontWeight: '600 !important',
                            marginTop: '1.5rem !important',
                            marginBottom: '0.75rem !important',
                            lineHeight: '1.4 !important',
                        },
                        '& ul, & ol': {
                            marginBottom: '1rem !important',
                            marginTop: '0.5rem !important',
                            paddingLeft: '2rem !important',
                            fontSize: '1rem !important',
                        },
                        '& li': {
                            marginBottom: '0.5rem !important',
                            marginTop: '0 !important',
                            lineHeight: '1.8 !important',
                            fontSize: '1rem !important',
                        },
                        '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            paddingLeft: '1rem !important',
                            paddingRight: '1rem !important',
                            paddingTop: '0.5rem !important',
                            paddingBottom: '0.5rem !important',
                            marginTop: '1rem !important',
                            marginBottom: '1rem !important',
                            marginLeft: '0 !important',
                            marginRight: '0 !important',
                            fontStyle: 'italic',
                            backgroundColor: 'grey.50',
                            fontSize: '1rem !important',
                        },
                        '& a': {
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontSize: 'inherit !important',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        },
                        '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginTop: '1.5rem !important',
                            marginBottom: '1.5rem !important',
                            marginLeft: '0 !important',
                            marginRight: '0 !important',
                            '& th, & td': {
                                border: '1px solid',
                                borderColor: 'divider',
                                padding: '0.75rem !important',
                                textAlign: 'left',
                                fontSize: '0.95rem !important',
                            },
                            '& th': {
                                backgroundColor: 'grey.100',
                                fontWeight: '600 !important',
                            },
                        },
                        // Primeiro elemento não tem margem superior
                        '& > *:first-of-type': {
                            marginTop: '0 !important',
                        },
                        // Último elemento não tem margem inferior
                        '& > *:last-child': {
                            marginBottom: '0 !important',
                        },
                    }}
                >
                    <Typography
                        component="div"
                        dangerouslySetInnerHTML={{ __html: article.content || '' }}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
